import './AdminPanel.css'
import adminApi from '../adminApi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Table, Space, Button, Modal, Form, Input, InputNumber, Checkbox, Select } from 'antd'

async function Post(apiKey, params) {
    await adminApi.post(apiKey, params, {
        headers: { 'Content-Type': 'application/json' },
    })
}

async function Put(apiKey, params) {
    await adminApi.put(apiKey, params, {
        headers: { 'Content-Type': 'application/json' },
    })
}

async function Delete(apiKey) {
    await adminApi.delete(apiKey, {
        headers: { 'Content-Type': 'application/json' },
    })
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

function parsePageImages(value) {
    if (!value) return []

    try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const productColumns = [
    {
        title: 'Фото',
        dataIndex: 'image',
        render: (value) => value ? <img className="adminPanel_thumb" src={value} alt="" /> : null,
    },
    {
        title: 'Название продукта',
        dataIndex: 'module',
    },
    {
        title: 'Описание',
        dataIndex: 'design',
    },
    {
        title: 'Базовая цена',
        dataIndex: 'price',
        render: (_, record) => record.info_only ? 'Скрыта' : `${record.price ?? 0} ₽`,
    },
    {
        title: 'Скрыт',
        dataIndex: 'hidden',
        render: (value) => value ? 'Да' : 'Нет',
    },
    {
        title: 'Продажа',
        dataIndex: 'info_only',
        render: (value) => value ? 'Только инфо' : 'Активна',
    },
    {
        title: 'Страница',
        dataIndex: 'page_content',
        render: (_, record) => (
            record.page_title || record.page_summary || record.page_content || record.page_images || record.page_use_cases || record.page_cta ? 'Да' : 'Нет'
        ),
    },
]

const featureColumns = [
    {
        title: 'Фича',
        dataIndex: 'name',
    },
    {
        title: 'Описание',
        dataIndex: 'description',
    },
    {
        title: 'Цена',
        dataIndex: 'price',
        render: (value) => `${value} ₽`,
    },
    {
        title: 'Стандарт',
        dataIndex: 'include_standard',
        render: (value) => value ? 'Да' : 'Нет',
    },
    {
        title: 'Премиум',
        dataIndex: 'include_premium',
        render: (value) => value ? 'Да' : 'Нет',
    },
    {
        title: 'Зависит от',
        dataIndex: 'required_feature_id',
        render: (value) => value ? `#${value}` : 'Нет',
    },
]

export default function AdminPanel({ apiKey }) {
    const [productKey, setProductKey] = useState()
    const [productData, setProductData] = useState([])
    const [productList, setProductList] = useState([])
    const [selectedProductKeys, setSelectedProductKeys] = useState([])
    const [isProductModalOpen, setIsProductModalOpen] = useState(false)
    const [isProductSubmitDisabled, setIsProductSubmitDisabled] = useState(true)
    const [productAction, setProductAction] = useState()
    const [imagePreview, setImagePreview] = useState("")
    const [pageImagePreviews, setPageImagePreviews] = useState([])
    const [productForm] = Form.useForm()

    const [featureKey, setFeatureKey] = useState()
    const [featureData, setFeatureData] = useState([])
    const [selectedFeatureKeys, setSelectedFeatureKeys] = useState([])
    const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false)
    const [isFeatureSubmitDisabled, setIsFeatureSubmitDisabled] = useState(true)
    const [featureAction, setFeatureAction] = useState()
    const [featureForm] = Form.useForm()
    const [settingsForm] = Form.useForm()

    const selectedProduct = useMemo(
        () => productList.find((product) => product.key === productKey),
        [productList, productKey]
    )
    const requiredFeatureOptions = useMemo(
        () => (selectedProduct?.features ?? [])
            .filter((feature) => feature.key !== featureKey)
            .map((feature) => ({
                value: feature.key,
                label: feature.name,
            })),
        [featureKey, selectedProduct]
    )

    const hasEmptyField = (fieldValues) => (
        Object.values(fieldValues).some(value =>
            value === "" || value === null || value === undefined
        )
    )

    const checkProductValue = () => {
        const values = productForm.getFieldsValue()

        setIsProductSubmitDisabled(hasEmptyField({
            module: values.module,
            design: values.design,
            image: values.image,
        }))
    }

    const checkFeatureValue = () => {
        const values = featureForm.getFieldsValue()
        setIsFeatureSubmitDisabled(!values.name || values.price === undefined || values.price === null)
    }

    const fetchProducts = useCallback(async () => {
        const resp = await adminApi.get(`${apiKey}/`)
        const products = resp.data.data ?? []

        setProductList(products)
        setProductData(products.find((product) => product.key === productKey) ?? [])
    }, [apiKey, productKey])

    useEffect(() => {
        fetchProducts()
    }, [fetchProducts])

    useEffect(() => {
        async function fetchSettings() {
            const resp = await adminApi.get(`${apiKey}/settings`)
            settingsForm.setFieldsValue(resp.data.data)
        }

        fetchSettings()
    }, [apiKey, settingsForm])

    const openProductCreateModal = () => {
        setIsProductModalOpen(true)
        productForm.resetFields()
        setImagePreview("")
        setPageImagePreviews([])
        setIsProductSubmitDisabled(true)
        setProductAction("create")
    }

    const openProductEditModal = () => {
        setIsProductModalOpen(true)
        productForm.setFieldsValue(productData)
        setImagePreview(productData.image ?? "")
        setPageImagePreviews(parsePageImages(productData.page_images))
        setIsProductSubmitDisabled(false)
        setProductAction("edit")
    }

    const handleImageChange = async (event) => {
        const file = event.target.files?.[0]
        if (!file) return

        const image = await fileToDataUrl(file)
        productForm.setFieldValue("image", image)
        setImagePreview(image)
        checkProductValue()
    }

    const handlePageImagesChange = async (event) => {
        const files = Array.from(event.target.files ?? [])
        if (files.length === 0) return

        const images = await Promise.all(files.map(fileToDataUrl))
        const currentImages = parsePageImages(productForm.getFieldValue("page_images"))
        const nextImages = [...currentImages, ...images]

        productForm.setFieldValue("page_images", JSON.stringify(nextImages))
        setPageImagePreviews(nextImages)
        event.target.value = ""
        checkProductValue()
    }

    const handleClearPageImages = () => {
        productForm.setFieldValue("page_images", "")
        setPageImagePreviews([])
        checkProductValue()
    }

    const handleProductOk = async (action) => {
        const values = {
            ...(await productForm.validateFields()),
            price: productForm.getFieldValue("price") ?? 0,
            hidden: productForm.getFieldValue("hidden") ?? false,
            info_only: productForm.getFieldValue("info_only") ?? false,
            license_hint: productForm.getFieldValue("license_hint") ?? "",
            features_hint: productForm.getFieldValue("features_hint") ?? "",
        }
        const url = `${apiKey}/`

        if (action === "create") {
            await Post(url, values)
        } else {
            await Put(`${url}${productKey}`, values)
        }

        await fetchProducts()
        setIsProductModalOpen(false)
    }

    const handleProductDelete = async () => {
        await Delete(`${apiKey}/${productKey}`)
        await fetchProducts()
        setSelectedProductKeys([])
        setProductKey(undefined)
        setProductData([])
        setSelectedFeatureKeys([])
        setFeatureKey(undefined)
        setFeatureData([])
    }

    const openFeatureCreateModal = () => {
        setIsFeatureModalOpen(true)
        featureForm.setFieldsValue({
            product_id: productKey,
            name: "",
            description: "",
            price: 0,
            include_standard: false,
            include_premium: true,
            required_feature_id: null,
        })
        setIsFeatureSubmitDisabled(true)
        setFeatureAction("create")
    }

    const openFeatureEditModal = () => {
        setIsFeatureModalOpen(true)
        featureForm.setFieldsValue(featureData)
        setIsFeatureSubmitDisabled(false)
        setFeatureAction("edit")
    }

    const handleFeatureOk = async (action) => {
        const values = await featureForm.validateFields()
        const payload = {
            ...values,
            product_id: productKey,
            include_standard: Boolean(values.include_standard),
            include_premium: Boolean(values.include_premium),
            required_feature_id: values.required_feature_id ?? null,
        }

        if (action === "create") {
            await Post(`${apiKey}/features/`, payload)
        } else {
            await Put(`${apiKey}/features/${featureKey}`, payload)
        }

        await fetchProducts()
        setIsFeatureModalOpen(false)
    }

    const handleFeatureDelete = async () => {
        await Delete(`${apiKey}/features/${featureKey}`)
        await fetchProducts()
        setSelectedFeatureKeys([])
        setFeatureKey(undefined)
        setFeatureData([])
    }

    const handleSettingsSave = async () => {
        const values = await settingsForm.validateFields()
        await Put(`${apiKey}/settings`, values)
    }

    return (
        <div className="adminPanel_content">
            <section className="adminPanel_settings">
                <h2>Настройки калькулятора</h2>
                <Form form={settingsForm} layout="vertical" className="adminPanel_settingsForm">
                    <Form.Item name="standard_support_percent" label="Стандартная техподдержка, %" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>

                    <Form.Item name="priority_support_percent" label="Приоритетная техподдержка, %" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>

                    <Form.Item name="setup_implementation" label="Внедрение, ₽" rules={[{ required: true }]}>
                        <InputNumber min={0} />
                    </Form.Item>

                    <Form.Item name="launch_implementation" hidden>
                        <InputNumber min={0} />
                    </Form.Item>

                    <Form.Item name="two_year_discount_percent" label="Скидка на 2 года, %" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>

                    <Form.Item name="perpetual_license_percent" label="Бессрочная лицензия, % от годовой" rules={[{ required: true }]}>
                        <InputNumber min={100} />
                    </Form.Item>

                    <Form.Item name="vat_percent" label="НДС, %" rules={[{ required: true }]}>
                        <InputNumber min={0} max={100} />
                    </Form.Item>

                    <Button type="primary" onClick={handleSettingsSave}>
                        Сохранить настройки
                    </Button>
                </Form>
            </section>

            <section className="adminPanel_products">
            <Space className="adminPanel_productsToolbar">
                <Button onClick={openProductCreateModal}>
                    Создать продукт
                </Button>
                <Button
                    onClick={openProductEditModal}
                    disabled={productKey === undefined || selectedProductKeys.length === 0}
                >
                    Редактировать
                </Button>
                <Button
                    disabled={productKey === undefined || selectedProductKeys.length === 0}
                    onClick={handleProductDelete}
                >
                    Удалить
                </Button>
            </Space>

            <Modal
                title="Продукт"
                open={isProductModalOpen}
                onOk={() => handleProductOk(productAction)}
                okButtonProps={{ disabled: isProductSubmitDisabled }}
                onCancel={() => setIsProductModalOpen(false)}
            >
                <Form
                    form={productForm}
                    name="product-form"
                    style={{ maxWidth: 600 }}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    onFieldsChange={checkProductValue}
                >
                    <Form.Item name="module" label="Название" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item name="design" label="Описание" rules={[{ required: true }]}>
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    <Form.Item name="price" label="Базовая цена">
                        <InputNumber min={0} addonAfter="₽ / человек" />
                    </Form.Item>

                    <Form.Item name="hidden" label="Скрыть с сайта" valuePropName="checked">
                        <Checkbox />
                    </Form.Item>

                    <Form.Item name="info_only" label="Только информация" valuePropName="checked">
                        <Checkbox />
                    </Form.Item>

                    <Form.Item name="license_hint" label="Хинт лицензии">
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    <Form.Item name="features_hint" label="Хинт фич">
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    <Form.Item name="image" label="Фото" rules={[{ required: true }]}>
                        <Input type="hidden" />
                    </Form.Item>

                    <div className="adminPanel_upload">
                        <input accept="image/*" type="file" onChange={handleImageChange} />
                        {imagePreview && <img src={imagePreview} alt="" />}
                    </div>

                    <div className="adminPanel_modalDivider">Страница продукта</div>

                    <Form.Item name="page_title" label="Заголовок">
                        <Input />
                    </Form.Item>

                    <Form.Item name="page_summary" label="Коротко">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="page_content" label="Описание">
                        <Input.TextArea rows={7} />
                    </Form.Item>

                    <Form.Item name="page_images" label="Скриншоты">
                        <Input type="hidden" />
                    </Form.Item>

                    <div className="adminPanel_pageImages">
                        <input accept="image/*" type="file" multiple onChange={handlePageImagesChange} />
                        {pageImagePreviews.length > 0 && (
                            <>
                                <div className="adminPanel_pageImagesGrid">
                                    {pageImagePreviews.map((image, index) => (
                                        <img src={image} alt="" key={`${image.slice(0, 32)}-${index}`} />
                                    ))}
                                </div>
                                <Button type="link" onClick={handleClearPageImages}>
                                    Очистить скриншоты
                                </Button>
                            </>
                        )}
                    </div>

                    <Form.Item name="page_use_cases" label="Сценарии">
                        <Input.TextArea
                            rows={4}
                            placeholder='[{"title":"Для проектных команд","description":"Короткое описание сценария"}]'
                        />
                    </Form.Item>

                    <Form.Item name="page_cta" label="CTA">
                        <Input.TextArea
                            rows={4}
                            placeholder='{"title":"Готовы внедрить продукт?","description":"Поможем подобрать конфигурацию.","primaryButtonText":"Рассчитать стоимость","secondaryButtonText":"Запросить демонстрацию"}'
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Table
                rowKey="key"
                className="adminPanel_table"
                style={{ width: "100%" }}
                rowSelection={{
                    type: "radio",
                    selectedRowKeys: selectedProductKeys,
                    onChange: (selectedRowKeys, selectedRows) => {
                        setSelectedProductKeys(selectedRowKeys)
                        setProductKey(selectedRowKeys[0])
                        setProductData(selectedRows[0])
                        setSelectedFeatureKeys([])
                        setFeatureKey(undefined)
                        setFeatureData([])
                    }
                }}
                columns={productColumns}
                dataSource={productList}
                align={"left"}
                bordered
                pagination={false}
            />
            </section>

            {selectedProduct && (
                <section className="adminPanel_features">
                    <div className="adminPanel_featuresHeader">
                        <h2>Фичи продукта: {selectedProduct.module}</h2>
                        <Space>
                            <Button onClick={openFeatureCreateModal}>
                                Добавить фичу
                            </Button>
                            <Button
                                onClick={openFeatureEditModal}
                                disabled={featureKey === undefined || selectedFeatureKeys.length === 0}
                            >
                                Редактировать
                            </Button>
                            <Button
                                disabled={featureKey === undefined || selectedFeatureKeys.length === 0}
                                onClick={handleFeatureDelete}
                            >
                                Удалить
                            </Button>
                        </Space>
                    </div>

                    <Table
                        rowKey="key"
                        className="adminPanel_table"
                        style={{ width: "100%" }}
                        rowSelection={{
                            type: "radio",
                            selectedRowKeys: selectedFeatureKeys,
                            onChange: (selectedRowKeys, selectedRows) => {
                                setSelectedFeatureKeys(selectedRowKeys)
                                setFeatureKey(selectedRowKeys[0])
                                setFeatureData(selectedRows[0])
                            }
                        }}
                        columns={featureColumns}
                        dataSource={selectedProduct.features ?? []}
                        align={"left"}
                        bordered
                        pagination={false}
                    />
                </section>
            )}

            <Modal
                title="Фича продукта"
                open={isFeatureModalOpen}
                onOk={() => handleFeatureOk(featureAction)}
                okButtonProps={{ disabled: isFeatureSubmitDisabled }}
                onCancel={() => setIsFeatureModalOpen(false)}
            >
                <Form
                    form={featureForm}
                    name="feature-form"
                    style={{ maxWidth: 600 }}
                    labelCol={{ span: 7 }}
                    wrapperCol={{ span: 17 }}
                    onFieldsChange={checkFeatureValue}
                >
                    <Form.Item name="product_id" hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item name="name" label="Название" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Form.Item name="description" label="Описание">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="price" label="Цена" rules={[{ required: true }]}>
                        <InputNumber min={0} addonAfter="₽ / человек" />
                    </Form.Item>

                    <Form.Item name="required_feature_id" label="Зависит от">
                        <Select
                            allowClear
                            options={requiredFeatureOptions}
                            placeholder="Без зависимости"
                        />
                    </Form.Item>

                    <Form.Item name="include_standard" label="Стандарт" valuePropName="checked">
                        <Checkbox>Входит в стандартную лицензию</Checkbox>
                    </Form.Item>

                    <Form.Item name="include_premium" label="Премиум" valuePropName="checked">
                        <Checkbox>Входит в премиум-лицензию</Checkbox>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}
